import { Collaboration as OriginalCollaboration } from "@tiptap/extension-collaboration";
import { TextSelection, type EditorState } from "@tiptap/pm/state";
import { yUndoPluginKey } from "@tiptap/y-tiptap";
import { runInAction } from "mobx";

import { createLogger } from "@/utils/logger";

import type { DocumentModel } from "@/models/DocumentModel";
import type { MatrixSelectionDump } from "@/models/MatrixSelectionModel";
import type { SelectionDump } from "@/models/SelectionModel";
import type { CollaborationOptions } from "@tiptap/extension-collaboration";
import type { Transaction, UndoManager } from "yjs";

export const DEFAULT_CAPTURE_TIMEOUT = 300;

type DocExtensionOptions = CollaborationOptions & {
  doc: DocumentModel;
};

type UndoManagerEvent = {
  type: "undo" | "redo";
  stackItem: {
    meta: Map<string, unknown>;
  };
  changedParentTypes: Transaction["changedParentTypes"];
};

type SelectionRange = { anchor: number; head: number };

type MathCursorState = {
  nodeId: string;
  index: number;
};

type PluginState = {
  textSelection?: SelectionRange;
  mathCursor?: MathCursorState;
  mathSelection?: SelectionDump;
  matrixSelection?: MatrixSelectionDump;
};

const logger = createLogger("Collaboration");

function getStackItemMeta(meta: Map<string, unknown>): PluginState {
  return {
    mathCursor: meta.get("mathCursor") as PluginState["mathCursor"],
    textSelection: meta.get("textSelection") as PluginState["textSelection"],
    mathSelection: meta.get("mathSelection") as PluginState["mathSelection"],
    matrixSelection: meta.get("matrixSelection") as PluginState["matrixSelection"],
  };
}

export function getUndoManager(state: EditorState): UndoManager {
  const pluginState = yUndoPluginKey.getState(state) as { undoManager: UndoManager };
  return pluginState.undoManager;
}

// Tags an undo step as an auto-applied text→math conversion, storing the text-cursor
// position to restore on undo. Centralised here (the undo plugin) so the suggestions
// code that creates the conversion and the input handler that reverts it share one
// definition; the restoration itself happens in stack-item-popped below.
const AUTO_MATH_UNDO_CURSOR_META = "autoMathUndoCursor";

/**
 * Tag the just-committed conversion's undo step so a single undo restores a plain text
 * cursor at `pos` (the end of the typed text) instead of re-selecting the converted
 * node's range.
 */
export function tagAutoMathUndoCursor(state: EditorState, pos: number): void {
  const { undoStack } = getUndoManager(state);
  undoStack[undoStack.length - 1]?.meta.set(AUTO_MATH_UNDO_CURSOR_META, pos);
}

/** Whether the top undo step is such an auto-applied math conversion. */
export function topUndoIsAutoMathConversion(state: EditorState): boolean {
  const { undoStack } = getUndoManager(state);
  return undoStack[undoStack.length - 1]?.meta.get(AUTO_MATH_UNDO_CURSOR_META) !== undefined;
}

// `lastChange` isn't part of the public UndoManager typings, but it's stable in yjs 13.
type UndoManagerInternals = UndoManager & { lastChange: number };

/**
 * Folds the next tracked change into the top undo step, however long after it the
 * change comes — for a gesture of many changes that should undo as one.
 */
export function joinNextChangeToUndoStep(state: EditorState): void {
  const undoManager = getUndoManager(state) as UndoManagerInternals;
  if (undoManager.undoStack.length > 0) {
    undoManager.lastChange = Date.now();
  }
}

export const Collaboration = OriginalCollaboration.extend<DocExtensionOptions, PluginState>({
  addStorage() {
    return {
      textSelection: undefined,
      mathCursor: undefined,
      mathSelection: undefined,
      matrixSelection: undefined,
    };
  },

  onCreate() {
    const { editor, options } = this;
    const { doc } = options;

    const undoManager = getUndoManager(editor.state);
    undoManager.addToScope(doc.yNodesMap);
    undoManager.addTrackedOrigin(doc);
    undoManager.captureTimeout = DEFAULT_CAPTURE_TIMEOUT;

    doc.yDoc?.on("beforeObserverCalls", (transaction: Transaction) => {
      /**
       * Fold transactions tagged with `preventNewUndoStack` (see DocumentModel.transact)
       * into the previous undo stack item instead of starting a new one. yjs's UndoManager
       * decides this by a capture-timeout check in its `afterTransaction` handler, so we
       * refresh `lastChange` in `beforeObserverCalls` — which fires after the transaction
       * body (so the meta is set) but before `afterTransaction` — to make the existing item
       * win.
       *
       * https://github.com/yjs/yjs/blob/main/src/utils/UndoManager.js#L227
       */
      if (transaction.meta.get("preventNewUndoStack") && undoManager.undoStack.length > 0) {
        (undoManager as UndoManagerInternals).lastChange = Date.now();
      }

      /**
       * Force a new undo stack item for transactions tagged with `forceNewUndoStack`
       * (the inverse of the above). Resetting `lastChange` to 0 makes yjs's capture-timeout
       * check fail in `afterTransaction`, so the change starts a fresh stack item instead of
       * folding into the previous one.
       */
      if (transaction.meta.get("forceNewUndoStack")) {
        (undoManager as UndoManagerInternals).lastChange = 0;
      }
    });

    doc.yDoc?.on("beforeTransaction", (e: Transaction) => {
      logger.debug("before ydoc transaction", `origin:`, e.origin);

      if (e.origin !== doc) return;

      this.storage.mathSelection = doc.selection.dumpSelection();
      this.storage.matrixSelection = doc.matrixSelection.dumpSelection();
      this.storage.mathCursor =
        doc.editor.editorState.isExpression && doc.cursor.node && doc.cursor.index !== undefined
          ? { nodeId: doc.cursor.node.id, index: doc.cursor.index }
          : undefined;

      // Capture textSelection for DocumentModel transactions (e.g., mixed content delete)
      const { anchor, head } = editor.state.selection;
      this.storage.textSelection = { anchor, head };
    });

    editor.on("beforeTransaction", ({ transaction }) => {
      if (!transaction.docChanged) return;

      const { anchor, head } = editor.state.selection;
      this.storage.textSelection = { anchor, head };
    });

    undoManager.on("stack-item-added", (event: UndoManagerEvent) => {
      logger.debug("added stack:", `\n${debugPrintEvent(event)}`);

      event.stackItem.meta.set("mathCursor", this.storage.mathCursor);
      event.stackItem.meta.set("textSelection", this.storage.textSelection);
      event.stackItem.meta.set("mathSelection", this.storage.mathSelection);
      event.stackItem.meta.set("matrixSelection", this.storage.matrixSelection);

      this.storage.textSelection = undefined;
      this.storage.mathCursor = undefined;
      this.storage.mathSelection = undefined;
      this.storage.matrixSelection = undefined;
    });

    undoManager.on("stack-item-updated", (event: UndoManagerEvent) => {
      logger.debug("updated stack:", `\n${debugPrintEvent(event)}`);
    });

    undoManager.on("stack-item-popped", (event: UndoManagerEvent) => {
      logger.debug("popped stack:", `\n${debugPrintEvent(event)}`);

      const stackMeta = getStackItemMeta(event.stackItem.meta);

      // Undoing an auto-applied math conversion should drop a plain cursor after the
      // typed text (tagged via tagAutoMathUndoCursor) instead of re-selecting the range
      // the converted node occupied. Only on undo — redo replays the same item and keeps
      // its node selection — so this reuses the text-selection restoration below rather
      // than the captured (node-range) value.
      const undoTextCursor = event.stackItem.meta.get(AUTO_MATH_UNDO_CURSOR_META) as
        | number
        | undefined;
      if (event.type === "undo" && undoTextCursor !== undefined) {
        stackMeta.textSelection = { anchor: undoTextCursor, head: undoTextCursor };
        stackMeta.mathCursor = undefined;
      }

      const { mathCursor, textSelection, mathSelection, matrixSelection } = stackMeta;
      const validMathCursor = mathCursor !== undefined;

      logger.debug("restore text selection", textSelection, event.stackItem);
      if (textSelection) {
        let chain = editor.chain();
        if (!validMathCursor) {
          doc.cursor.reset();
          chain = chain.focus();
        }
        chain
          .command(({ tr }) => {
            // we use custom command for selection because setTextSelection() can only select forward
            // (head after anchor), but we want to also support "backward" selection
            if (
              textSelection.anchor < tr.doc.content.size &&
              textSelection.head < tr.doc.content.size
            ) {
              tr.setSelection(
                TextSelection.create(tr.doc, textSelection.anchor, textSelection.head),
              );
            } else {
              logger.warn(
                "Cannot restore text selection, out of bounds",
                textSelection,
                tr.doc.content.size,
              );
            }
            return true;
          })
          .run();
      }

      logger.debug("restore math cursor position", mathCursor);

      if (validMathCursor) {
        runInAction(() => {
          doc.cursor.moveTo(mathCursor.nodeId, mathCursor.index);
        });
      }

      logger.debug("restore node selection", mathSelection);
      if (mathSelection) {
        doc.selection.restoreSelection(mathSelection);
      }

      logger.debug("restore matrix selection", matrixSelection);
      doc.matrixSelection.restoreSelection(matrixSelection);

      doc.editor.suggestions.reset();
    });
  },
});

function debugPrintEvent(event: UndoManagerEvent): string {
  try {
    const lines: string[] = [
      `Type: ${event.type}`,
      `Changed parent types count: ${event.changedParentTypes.size}`,
    ];

    let index = 0;
    for (const [key, val] of event.changedParentTypes.entries()) {
      const typeName = key.constructor.name;
      lines.push(`\n[${index}] ${typeName}:`);

      const ev = val[0];
      if (ev?.changes?.delta) {
        lines.push(`  Delta changes:`);
        for (const delta of ev.changes.delta) {
          for (const [k, v] of Object.entries(delta)) {
            lines.push(`    ${k} => ${JSON.stringify(v)}`);
          }
        }
      }

      if (ev?.changes?.keys) {
        const { keys } = ev.changes;
        lines.push(`  Key changes (${keys.size}):`);
        for (const [k, v] of keys.entries()) {
          lines.push(`    ${k}: ${v.action}`);
        }
      }

      index += 1;
    }

    return lines.join("\n");
  } catch (e) {
    console.error(e);
    return JSON.stringify(event);
  }
}
